import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { View, ScrollView, Text, StyleSheet, TouchableOpacity, TextInput, ActivityIndicator, Image, Modal, Platform } from 'react-native';
import { supabase as supabaseClient } from '@cubemeet/core';
import PlatformIcon from '../../components/common/PlatformIcon';
import { useOverpassPois } from '../../hooks/useOverpassPois';
import { ORANGE_ACTION } from '../../constants/colors';
import NearbyMap, { NearbySpot } from '../NearbyMap';

// Helper to extract coordinates (ported from CardListPage for consistency)
const extractCoords = (card: any): { lat: number; lng: number } | undefined => {
  if (!card) return undefined;
  
  const tryObj = (obj: any) => {
    if (!obj) return undefined;
    
    const parseNum = (v: any) => {
      if (typeof v === 'number') return v;
      if (typeof v === 'string' && v.trim()) {
        const n = Number(v);
        return Number.isNaN(n) ? undefined : n;
      }
      return undefined;
    };

    // Handle array [lng, lat] or [lat, lng] directly
    if (Array.isArray(obj) && obj.length >= 2) {
       const n1 = parseNum(obj[0]);
       const n2 = parseNum(obj[1]);
       if (n1 !== undefined && n2 !== undefined) {
          // Heuristic: if abs(n1) > 90, it's likely lng.
          if (Math.abs(n1) > 90) return { lat: n2, lng: n1 };
          return { lat: n1, lng: n2 };
       }
    }

    // common shapes: { lat, lng } or { latitude, longitude } or { lat, lon }
    const lat1 = parseNum(obj.lat);
    const lng1 = parseNum(obj.lng) ?? parseNum(obj.lon);
    if (lat1 !== undefined && lng1 !== undefined) return { lat: lat1, lng: lng1 };

    const lat2 = parseNum(obj.latitude);
    const lng2 = parseNum(obj.longitude);
    if (lat2 !== undefined && lng2 !== undefined) return { lat: lat2, lng: lng2 };

    // coordinates array inside object
    if (Array.isArray(obj.coordinates) && obj.coordinates.length >= 2) {
      const a = obj.coordinates;
      const maybeLng = parseNum(a[0]);
      const maybeLat = parseNum(a[1]);
      if (maybeLat !== undefined && maybeLng !== undefined) return { lat: maybeLat, lng: maybeLng };
    }
    
    // string like "lat,lng" or "lng,lat"
    if (typeof obj === 'string' && obj.indexOf(',') !== -1) {
      const parts = obj.split(',').map(s => s.trim()).map(Number);
      if (parts.length >= 2 && parts.every(p => !Number.isNaN(p))) {
        if (Math.abs(parts[0]) <= 90) return { lat: parts[0], lng: parts[1] };
        return { lat: parts[1], lng: parts[0] };
      }
    }
    return undefined;
  };

  // check typical locations
  const candidates = [
    card, 
    card.base_payload, 
    card.custom_payload,
    card.base_payload?.location, 
    card.custom_payload?.location, 
    card.location, 
    card.address,
    card.base_payload?.geometry
  ];
  for (const c of candidates) {
    const r = tryObj(c);
    if (r) return r;
  }
  return undefined;
};

// 标签类型定义
interface TagFilter {
  type: 'tags' | 'category' | 'subcategory' | 'custom' | 'location';
  values: string[];
  label: string;
}

export interface CardListProps {
  initialTagFilters?: TagFilter[];
  selectedTypes?: string[];
  selectedRegions?: string[];
  sortBy?: 'created_at' | 'popularity' | 'rating';
  viewMode?: 'grid' | 'list';
  title?: string;
  embedded?: boolean;
  onSelectCard?: (card: Card) => void;
  onPageChange?: (nextPage: number, meta?: { recommendedScrollTop?: number }) => void;
  openDetailInModal?: boolean;
  showAddButton?: boolean;
  // 供外部（如 bottom-sheet）传入内边距，避免底部内容被遮挡
  bottomInset?: number;
  /**
   * 是否在图片上显示悬浮的「+」按钮。
   * 默认关闭（false），以免遮挡缩略图。
   * 只有在特定页面需要时才显式开启。
   */
  showAddOnImage?: boolean;
  pickerMode?: boolean; // 自动开启 list + showAddButton + embedded
}

export interface Card {
  id: string;
  title: string;
  description?: string;
  address?: string;
  card_type: string;
  tags?: string[];
  category?: string;
  subcategory?: string;
  cover_image?: string;
  created_at: string;
  creator_id: string;
  visibility: string;
  status: string;
  base_payload?: any;
  location?: any;
  latitude?: number;
  longitude?: number;
}

const CardList: React.FC<CardListProps> = (props) => {
  const {
    initialTagFilters: initialTagFiltersProp,
    selectedTypes: selectedTypesProp,
    selectedRegions: selectedRegionsProp,
    sortBy: sortByProp = 'created_at',
    viewMode: viewModeProp = 'grid',
    title: titleProp = '卡片浏览',
    embedded: embeddedProp = false,
    onSelectCard,
    onPageChange,
    openDetailInModal = false,
    showAddButton: showAddButtonProp = false,
    bottomInset,
    showAddOnImage = false,
    pickerMode = false,
  } = props;

  // pickerMode 下的默认覆写
  const embedded = pickerMode ? true : embeddedProp;
  const currentViewModeDefault: 'grid' | 'list' = pickerMode ? 'list' : viewModeProp;
  const showAddButton = pickerMode ? true : showAddButtonProp;
  const title = pickerMode ? (titleProp || '从卡片库选择') : titleProp;

  const initialTagFilters = useMemo(() => initialTagFiltersProp ?? [], [initialTagFiltersProp]);
  const selectedTypes = useMemo(() => selectedTypesProp ?? [], [selectedTypesProp]);
  const selectedRegions = useMemo(() => selectedRegionsProp ?? [], [selectedRegionsProp]);
  const sortBy = sortByProp;

  const [searchQuery, setSearchQuery] = useState('');
  const [cards, setCards] = useState<Card[]>([]);
  const [loading, setLoading] = useState(true);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [availableCities, setAvailableCities] = useState<string[]>([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [showCityFilter, setShowCityFilter] = useState(false);

  // Normalize city strings when used for querying to avoid subtle mismatches
  const normalizeCity = (input?: string) => {
    if (!input || typeof input !== 'string') return input;
    try {
      return input.trim().normalize ? input.trim().normalize('NFKC') : input.trim();
    } catch (e) { return input.trim(); }
  };

  // OSM / Overpass quick preview (client-side, for demo/testing)
  // Default to false to avoid spamming public Overpass instances. Enable manually for preview.
  const [osmEnabled, setOsmEnabled] = useState(false);
  const [osmCity, setOsmCity] = useState('Taipei');
  const [osmTypes, setOsmTypes] = useState<string[]>(['poi']);
  const [osmRetry, setOsmRetry] = useState(0);
  const [osmBbox, setOsmBbox] = useState<string | undefined>(undefined);
  const [osmCenter, setOsmCenter] = useState<{ lat: number; lon: number } | null>(null);
  const [osmLatInput, setOsmLatInput] = useState('');
  const [osmLonInput, setOsmLonInput] = useState('');
  const [osmRadius, setOsmRadius] = useState('1500');
  const [osmGeoStatus, setOsmGeoStatus] = useState<'idle' | 'locating'>('idle');
  const [osmGeoError, setOsmGeoError] = useState<string | null>(null);
  // Local demo OSM cards (used when public Overpass is blocked or you want offline preview)
  const demoOsmCards: Card[] = [
    {
      id: 'osm:node:1',
      title: '示例餐厅 — 小吃店',
      description: '这是一个示例餐厅，用于本地预览 OSM 卡片。',
      address: '台北市中正區示例路 1 號',
      card_type: 'restaurant',
      tags: ['示例', '餐廳'],
      category: 'food',
      subcategory: 'local',
      cover_image: '',
      created_at: new Date().toISOString(),
      creator_id: 'osm',
      visibility: 'public',
      status: 'active',
    },
    {
      id: 'osm:node:2',
      title: '示例景點 — 小公園',
      description: '示例景點，供介面測試使用。',
      address: '台北市大安區示例巷 2 號',
      card_type: 'poi',
      tags: ['示例', '景點'],
      category: 'sight',
      subcategory: 'park',
      cover_image: '',
      created_at: new Date().toISOString(),
      creator_id: 'osm',
      visibility: 'public',
      status: 'active',
    },
  ];

  const [activeTagFilters, setActiveTagFilters] = useState<TagFilter[]>(initialTagFilters);
  const [localSelectedTypes, setLocalSelectedTypes] = useState<string[]>(selectedTypes);
  const [currentViewMode, setCurrentViewMode] = useState<'grid'|'list'>(currentViewModeDefault);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [maxVisiblePages, setMaxVisiblePages] = useState(6);
  const listTopRef = useRef<any>(null);
  const [filtersAnchorY, setFiltersAnchorY] = useState(0);
  const FILTER_SCROLL_OFFSET = 36;
  const [paging, setPaging] = useState(false);
  const moduleRef = useRef<any>(null);
  const lastRequestIdRef = useRef(0);

  const scrollToFilterAnchor = useCallback(() => {
    const scrollNode = listTopRef.current;
    if (!scrollNode || typeof filtersAnchorY !== 'number') return;
    const target = Math.max(filtersAnchorY + FILTER_SCROLL_OFFSET, 0);
    try {
      scrollNode.scrollTo?.({ y: target, animated: true });
    } catch (err) {
      try { scrollNode.scrollTo?.(0, target); } catch {}
    }
  }, [FILTER_SCROLL_OFFSET, filtersAnchorY]);

  useEffect(() => {
    let active = true;
    const loadUser = async () => {
      try {
        if (supabaseClient?.auth?.getUser) {
          const { data } = await supabaseClient.auth.getUser();
          if (active) setCurrentUserId(data?.user?.id ?? null);
        }
      } catch {
        if (active) setCurrentUserId(null);
      }
    };
    loadUser();

    let subscription: { unsubscribe: () => void } | undefined;
    if (supabaseClient?.auth?.onAuthStateChange) {
      const { data } = supabaseClient.auth.onAuthStateChange((_event, session) => {
        if (!active) return;
        setCurrentUserId(session?.user?.id ?? null);
      });
      subscription = data?.subscription;
    }

    return () => { active = false; if (subscription) subscription.unsubscribe(); };
  }, []);

  // Card types
  const cardTypes = [
    { key: 'poi', label: '景点', icon: 'place' },
    { key: 'restaurant', label: '餐厅', icon: 'restaurant' },
    { key: 'lodging', label: '住宿', icon: 'hotel' },
    { key: 'activity', label: '活动', icon: 'event' },
    { key: 'shopping', label: '购物', icon: 'shopping-cart' },
  ];

  const applyVisibilityFilter = (query: any, userId?: string | null) => {
    if (userId) {
      const safeUserId = String(userId).replace(/[(),]/g, '');
      return query.or(`visibility.eq.public,and(visibility.eq.private,creator_id.eq.${safeUserId})`);
    }
    return query.eq('visibility', 'public');
  };

  // Fetch available tags (simplified)
  const fetchAvailableOptions = async () => {
    try {
      let query: any = supabaseClient.from('cards_cards').select('tags').eq('status', 'active');
      query = applyVisibilityFilter(query, currentUserId);
      const { data } = await query;
      if (data) {
        const allTags = new Set<string>();
        data.forEach((card: any) => { if (Array.isArray(card.tags)) card.tags.forEach((t: string) => allTags.add(t)); });
        setAvailableTags(Array.from(allTags).sort());
      }
    } catch (e) { console.error('Failed to fetch available options:', e); }
  };

  // Fetch available cities from location field
  const fetchAvailableCities = async () => {
    try {
      let query: any = supabaseClient.from('cards_cards').select('location').eq('status', 'active');
      query = applyVisibilityFilter(query, currentUserId);
      const { data } = await query;
      if (data) {
        const citiesSet = new Set<string>();
        data.forEach((card: any) => {
          if (card.location && typeof card.location === 'object' && card.location.city) {
            // store normalized city values to keep query semantics consistent
            citiesSet.add(normalizeCity(card.location.city) as string);
          }
        });
        setAvailableCities(Array.from(citiesSet).sort());
      }
    } catch (e) { console.error('Failed to fetch available cities:', e); }
  };

  const filtersRef = useRef({ initialTagFilters, searchQuery, localSelectedTypes, sortBy, currentUserId, selectedCities });
  useEffect(() => {
    filtersRef.current = { initialTagFilters, searchQuery, localSelectedTypes, sortBy, currentUserId, selectedCities };
  }, [initialTagFilters, searchQuery, localSelectedTypes, sortBy, currentUserId, selectedCities]);

  // call Overpass hook (fast client-side demo). disabled by default to avoid accidental public API usage.
  // pass `osmRetry` as fetchKey to force a retry when user clicks retry.
  // NOTE: allowPublicEndpoint=true 会允许直接请求公共 Overpass（仅用于本地测试）
  const radiusNumber = parseFloat(osmRadius);
  const centerRadius = Number.isFinite(radiusNumber) && radiusNumber > 0
    ? Math.max(100, Math.min(radiusNumber, 20000))
    : undefined;

  const { data: osmData, loading: osmLoading, error: osmError } = useOverpassPois({
    city: osmCenter ? undefined : osmCity,
    types: osmTypes,
    enabled: osmEnabled,
    fetchKey: osmRetry,
    allowPublicEndpoint: true,
    bbox: osmBbox,
    center: osmCenter ? { lat: osmCenter.lat, lon: osmCenter.lon, radius: centerRadius } : undefined,
  });

  const notifyPageChange = useCallback((nextPage: number, meta?: { recommendedScrollTop?: number }) => {
    if (typeof onPageChange !== 'function') return;
    try {
      onPageChange(nextPage, meta);
    } catch (err) {
      console.error('[CardList] onPageChange listener failed', err);
    }
  }, [onPageChange]);

  const fetchCards = useCallback(async (pageNum = 1, reset = false, showLoading = true, options?: { notify?: boolean }) => {
    const requestId = ++lastRequestIdRef.current;
    if (options?.notify) setPaging(true);
    try {
      const { initialTagFilters, searchQuery, localSelectedTypes, sortBy, currentUserId, selectedCities } = filtersRef.current as any;
      const normalizedSelectedCities = Array.isArray(selectedCities) ? selectedCities.map((c: string) => normalizeCity(c)) : [];
      if (showLoading) setLoading(true);
      const limit = 12;

      let countQuery: any = supabaseClient.from('cards_cards').select('id', { count: 'exact' }).eq('status', 'active');
      countQuery = applyVisibilityFilter(countQuery, currentUserId);
      if (searchQuery.trim()) countQuery = countQuery.or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%,address.ilike.%${searchQuery}%`);
      if (localSelectedTypes.length > 0) countQuery = countQuery.in('card_type', localSelectedTypes);
      if (selectedCities.length > 0) {
        // Apply city filter using JSONB query (use normalized values)
        const cityConditions = normalizedSelectedCities.map((city: string) => `location->>city.eq.${city}`).join(',');
        countQuery = countQuery.or(cityConditions);
      }

      const { count: cnt } = await countQuery;
      const total = typeof cnt === 'number' ? cnt : 0;
      setTotalCount(total);
      setTotalPages(total > 0 ? Math.ceil(total / limit) : 0);

      let query: any = supabaseClient.from('cards_cards').select('id, title, description, address, card_type, tags, category, subcategory, cover_image, created_at, creator_id, visibility, status, base_payload, location, latitude, longitude').eq('status', 'active');
      query = applyVisibilityFilter(query, currentUserId);
      if (searchQuery.trim()) query = query.or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%,address.ilike.%${searchQuery}%`);
      if (localSelectedTypes.length > 0) query = query.in('card_type', localSelectedTypes);
      if (selectedCities.length > 0) {
        const cityConditions = normalizedSelectedCities.map((city: string) => `location->>city.eq.${city}`).join(',');
        query = query.or(cityConditions);
      }

      switch (sortBy) {
        case 'created_at':
        case 'popularity':
        case 'rating':
          query = query.order('created_at', { ascending: false });
          break;
      }

      const offset = (pageNum - 1) * limit;
      query = query.range(offset, offset + limit - 1);
      const { data, error } = await query;

      if (requestId !== lastRequestIdRef.current) return;

      if (!error && data) {
        console.log('===FETCHED CARDS===', data.length, 'cards');
        // 找出視頻卡並打印
        const videoCards = data.filter((c: any) => c.card_type === 'video');
        console.log('===VIDEO CARDS===', videoCards.length, videoCards);
        if (videoCards.length > 0) {
          console.log('===FIRST VIDEO CARD DETAIL===', {
            id: videoCards[0].id,
            title: videoCards[0].title,
            card_type: videoCards[0].card_type,
            hasBasePayload: !!videoCards[0].base_payload,
            basePayload: videoCards[0].base_payload,
          });
        }
        
        if (reset || pageNum === 1) setCards(data);
        else setCards(prev => [...prev, ...data]);
        setHasMore(data.length === limit);
        if (options?.notify) {
          scrollToFilterAnchor();
          notifyPageChange(pageNum, { recommendedScrollTop: filtersAnchorY + FILTER_SCROLL_OFFSET });
        }
      } else {
        console.error('Failed to fetch cards:', error);
      }
    } catch (e) { console.error('Error fetching cards:', e); } finally { 
      if (requestId === lastRequestIdRef.current) {
        setLoading(false);
        if (options?.notify) setPaging(false);
      }
    }
  }, [FILTER_SCROLL_OFFSET, filtersAnchorY, notifyPageChange, scrollToFilterAnchor]);

  useEffect(() => { fetchAvailableOptions(); fetchAvailableCities(); fetchCards(1, true); }, [searchQuery, localSelectedTypes, sortBy, selectedCities, currentUserId]);

  useEffect(() => {
    const calculate = () => {
      if (typeof window === 'undefined') return;
      const w = window.innerWidth;
      if (w < 480) setMaxVisiblePages(3);
      else if (w < 900) setMaxVisiblePages(5);
      else setMaxVisiblePages(6);
    };
    calculate();
    window.addEventListener('resize', calculate);
    return () => window.removeEventListener('resize', calculate);
  }, []);

  const handleTypeToggle = (type: string) => {
    setLocalSelectedTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  };

  const handleUseCurrentLocation = () => {
    const geoNavigator = typeof globalThis !== 'undefined' ? (globalThis as any).navigator : undefined;
    if (!geoNavigator || !geoNavigator.geolocation) {
      setOsmGeoError('当前环境无法获取定位信息');
      return;
    }
    setOsmGeoStatus('locating');
    setOsmGeoError(null);
    try {
      geoNavigator.geolocation.getCurrentPosition(
        (position: any) => {
          const coords = position?.coords || {};
          const latitude = typeof coords.latitude === 'number' ? coords.latitude : NaN;
          const longitude = typeof coords.longitude === 'number' ? coords.longitude : NaN;
          if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
            setOsmGeoStatus('idle');
            setOsmGeoError('定位结果无效');
            return;
          }
          const latFixed = Number(latitude.toFixed(6));
          const lonFixed = Number(longitude.toFixed(6));
          setOsmCenter({ lat: latFixed, lon: lonFixed });
          setOsmLatInput(latFixed.toString());
          setOsmLonInput(lonFixed.toString());
          setOsmBbox(undefined);
          setOsmGeoStatus('idle');
          if (!osmEnabled) setOsmEnabled(true);
          setOsmRetry(r => r + 1);
        },
        (err: any) => {
          setOsmGeoStatus('idle');
          setOsmGeoError(err?.message ? String(err.message) : '定位失败');
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
      );
    } catch (err: any) {
      setOsmGeoStatus('idle');
      setOsmGeoError(err?.message ? String(err.message) : '定位失败');
    }
  };

  const handleApplyManualCenter = () => {
    const lat = parseFloat(osmLatInput);
    const lon = parseFloat(osmLonInput);
    if (Number.isNaN(lat) || Number.isNaN(lon)) {
      setOsmGeoError('请输入有效的经纬度');
      return;
    }
    setOsmCenter({ lat, lon });
    setOsmBbox(undefined);
    setOsmGeoError(null);
    if (!osmEnabled) setOsmEnabled(true);
    setOsmRetry(r => r + 1);
  };

  const handleClearCenter = () => {
    setOsmCenter(null);
    setOsmLatInput('');
    setOsmLonInput('');
    setOsmGeoError(null);
    setOsmGeoStatus('idle');
    setOsmEnabled(false);
  };

  const handleCardPress = (card: Card) => {
    if (openDetailInModal) {
      setDetailCard(card);
      setDetailModalVisible(true);
      return;
    }

    if (String(card.id).startsWith('osm:')) {
      const parts = String(card.id).split(':');
      if (parts.length === 3) {
        const [, osmType, osmId] = parts;
        try { if (typeof window !== 'undefined') window.open(`https://www.openstreetmap.org/${osmType}/${osmId}`, '_blank', 'noopener'); } catch {}
        return;
      }
    }

    try { if (typeof window !== 'undefined') window.location.href = `/card/${card.id}`; } catch {}
  };

  const loadMore = () => { if (!loading && hasMore) { const nextPage = page + 1; setPage(nextPage); fetchCards(nextPage, false); } };

  const getPageWindow = (total: number, current: number) => {
    const maxVisible = Math.max(1, Math.min(total, maxVisiblePages || 5));
    if (total <= maxVisible) return Array.from({ length: total }).map((_, i) => i + 1);
    let start = current - Math.floor(maxVisible / 2);
    if (start < 1) start = 1;
    let end = start + maxVisible - 1;
    if (end > total) { end = total; start = Math.max(1, end - maxVisible + 1); }
    return Array.from({ length: end - start + 1 }).map((_, i) => start + i);
  };

  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailCard, setDetailCard] = useState<Card | null>(null);

  const displayedCards = useMemo(() => {
    if (!osmEnabled || !Array.isArray(osmData) || osmData.length === 0) return cards;
    const ids = new Set(cards.map(c => c.id));
    const extra = osmData.filter((o: any) => !ids.has(o.id));
    return [...extra, ...cards];
  }, [osmEnabled, osmData, cards]);

  const renderCard = (card: Card) => {
    const isGridMode = currentViewMode === 'grid';
    const isPrivateOwned = card.visibility === 'private' && card.creator_id === currentUserId;
    const embedData = card.base_payload?.embed;
    // 判断是否为视频卡片：类型为 video 或者 payload 中包含视频源
    const isVideoCard = card.card_type === 'video' || !!(embedData && embedData.src);
    
    // Debug: 打印所有卡片類型
    console.log('===CARD===', card.id, 'type:', card.card_type, 'title:', card.title);
    
    const BROKEN_PLACEHOLDER = '/assets/placeholders/card-header-default.jpg';
    
    // 辅助函数：检查图片 URL 是否有效
    const isValidImage = (url?: string) => url && url !== BROKEN_PLACEHOLDER;
    
    // 统一获取显示图片：优先 cover_image，其次 base_payload.imageUrl，最后 embed.poster
    let displayImage = isValidImage(card.cover_image) ? card.cover_image :
                       isValidImage(card.base_payload?.imageUrl) ? card.base_payload?.imageUrl :
                       isValidImage(embedData?.poster) ? embedData?.poster : null;

    // Try to extract coordinates using the robust helper
    const coords = extractCoords(card);

    // Debug: Log image resolution for specific cards (e.g. hotels)
    if (card.card_type === 'lodging' || card.card_type === 'poi') {
      console.log('[CardList] Resolving image for:', card.title, {
        id: card.id,
        cover_image: card.cover_image,
        payloadImage: card.base_payload?.imageUrl,
        embedPoster: embedData?.poster,
        resolvedDisplayImage: displayImage,
        coords,
        pickerMode
      });
    }
    
    return (
      <TouchableOpacity key={card.id} style={[styles.cardItem, isGridMode ? styles.cardItemGrid : styles.cardItemList]} onPress={() => handleCardPress(card)}>
        <View style={[styles.cardCover, isGridMode ? styles.cardCoverGrid : styles.cardCoverList]}>
          {isVideoCard ? (
            // 視頻卡：顯示縮略圖 + 播放按鈕覆蓋層
            <View style={{ width: '100%', height: '100%', position: 'relative' }}>
              {displayImage ? (
                <Image source={{ uri: displayImage }} style={{ width: '100%', height: '100%' }} resizeMode="cover" />
              ) : (
                <View style={styles.cardImagePlaceholder}><PlatformIcon name="videocam" size={32} color="#9CA3AF" /></View>
              )}
              {/* 播放按鈕覆蓋層 */}
              <View style={styles.videoPlayOverlay}>
                <View style={styles.videoPlayButton}>
                  <PlatformIcon name="play-arrow" size={32} color="#fff" family="MaterialIcons" />
                </View>
              </View>
            </View>
          ) : displayImage ? (
            <Image source={{ uri: displayImage }} style={{ width: '100%', height: '100%' }} resizeMode="cover" />
          ) : (!isVideoCard && coords) ? (
            <View style={{ width: '100%', height: '100%', position: 'relative' }}>
               <NearbyMap
                 center={[coords.lat, coords.lng]}
                 spots={[{
                   id: card.id,
                   name: card.title,
                   lat: coords.lat,
                   lng: coords.lng,
                   address: card.address,
                   category: card.card_type,
                   source: 'card'
                 }]}
                 height="100%"
                 showControls={false}
                 showFullscreenButton={false}
                 embedded={true}
                 enableOSM={false}
                 showInterPointDistances={false}
               />
               <View style={{position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10, backgroundColor: 'transparent'}} />
            </View>
          ) : (
            <View style={styles.cardImagePlaceholder}><PlatformIcon name="image" size={24} color="#9CA3AF" /></View>
          )}
          {/* OSM badge for items coming from Overpass (id prefixed with "osm:") */}
          {String(card.id).startsWith('osm:') && (<View style={styles.osmBadge}><Text style={styles.osmBadgeText}>OSM</Text></View>)}
          
          <View style={styles.cardTypeBadge}><PlatformIcon name={cardTypes.find(t => t.key === card.card_type)?.icon || 'place'} size={12} color="#fff" /></View>
          {isPrivateOwned && (<View style={styles.privateBadge}><PlatformIcon name="lock" size={12} color="#fff" family="MaterialIcons" /></View>)}
          {showAddOnImage && onSelectCard && showAddButton && (
            <TouchableOpacity accessibilityLabel="加入行程" accessibilityRole="button" style={styles.addFloatBtn} activeOpacity={0.9} onPress={(ev?: any) => { try { ev?.stopPropagation?.(); } catch {} onSelectCard(card); }}>
              <PlatformIcon name="add" size={16} color="#fff" />
            </TouchableOpacity>
          )}
        </View>
        <View style={[styles.cardInfo, !isGridMode && styles.cardInfoListWithPill]}>
          <Text style={styles.cardTitle} numberOfLines={2}>{card.title || '未命名卡片'}</Text>
          {card.address && (
            <View style={styles.cardAddress}>
              <PlatformIcon name="location_on" size={16} color="#6B7280" family="MaterialIcons" />
              <Text style={styles.cardAddressText} numberOfLines={1}>{card.address}</Text>
            </View>
          )}
          <View style={styles.cardTagsContainer}>{Array.isArray(card.tags) && card.tags.length > 0 && (<View style={styles.cardTags}>{card.tags.slice(0, 2).map((tag, i) => (<View key={i} style={styles.cardTag}><Text style={styles.cardTagText}>#{tag}</Text></View>))}{card.tags.length > 2 && <Text style={styles.cardTagMore}>+{card.tags.length - 2}</Text>}</View>)}</View>
        </View>

        {!isGridMode && (onSelectCard || showAddButton) && (
          // move pointerEvents into style to avoid deprecated prop usage on web
          <View style={styles.listAddWrap}>
            <TouchableOpacity accessibilityLabel="加入行程" accessibilityRole="button" onPress={(ev?: any) => { try { ev?.stopPropagation?.(); } catch {} if (onSelectCard) onSelectCard(card); }} activeOpacity={0.9} style={styles.listAddPill}>
              <PlatformIcon name="add" size={16} color="#FFFFFF" />
            </TouchableOpacity>
          </View>
        )}
      </TouchableOpacity>
    );
  };

  return (
    <View ref={moduleRef} style={styles.container}>
      <View style={[styles.header, embedded && styles.headerEmbedded]}>
        <View style={styles.titleRow}><View style={[styles.titleBar, { backgroundColor: ORANGE_ACTION }]} /><Text style={[styles.title, embedded && styles.titleEmbedded]}>{title}</Text></View>
        <View style={[styles.viewModeToggle, embedded && styles.viewModeToggleEmbedded]}>
          <TouchableOpacity style={[styles.viewModeBtn, embedded && styles.viewModeBtnEmbedded, currentViewMode === 'grid' && styles.viewModeBtnActive]} onPress={() => setCurrentViewMode('grid')}>
            <PlatformIcon name="grid_view" size={embedded ? 14 : 20} color={currentViewMode === 'grid' ? '#0B61D6' : '#6B7280'} />
          </TouchableOpacity>
          <TouchableOpacity style={[styles.viewModeBtn, embedded && styles.viewModeBtnEmbedded, currentViewMode === 'list' && styles.viewModeBtnActive]} onPress={() => setCurrentViewMode('list')}>
            <PlatformIcon name="view_list" size={embedded ? 14 : 20} color={currentViewMode === 'list' ? '#0B61D6' : '#6B7280'} />
          </TouchableOpacity>
        </View>
      </View>

      <View style={styles.inner}>
        <View style={styles.searchContainer}>
          <View style={styles.searchRow}>
            <View style={styles.searchBox}><PlatformIcon name="search" size={20} color="#6B7280" /><TextInput style={styles.searchInput} placeholder="搜索卡片..." value={searchQuery} onChangeText={setSearchQuery} /></View>
            <TouchableOpacity 
              style={[styles.filterButton, selectedCities.length > 0 && styles.filterButtonActive]}
              onPress={() => setShowCityFilter(!showCityFilter)}
              accessibilityLabel="城市过滤"
            >
              <PlatformIcon name="tune" size={20} color={selectedCities.length > 0 ? '#0B61D6' : '#6B7280'} family="MaterialIcons" />
              {selectedCities.length > 0 && (
                <View style={styles.filterBadge}>
                  <Text style={styles.filterBadgeText}>{selectedCities.length}</Text>
                </View>
              )}
            </TouchableOpacity>
          </View>
          {showCityFilter && (
            <View style={styles.cityFilterContainer}>
              <View style={styles.cityFilterHeader}>
                <TouchableOpacity onPress={() => setShowCityFilter(false)}>
                  <PlatformIcon name="close" size={20} color="#6B7280" family="MaterialIcons" />
                </TouchableOpacity>
              </View>
              <View style={styles.cityChipsWrap}>
                {availableCities.map((city) => {
                  const isSelected = selectedCities.includes(city);
                  return (
                    <TouchableOpacity
                      key={city}
                      style={[styles.cityChip, isSelected && styles.cityChipSelected]}
                      onPress={() => {
                        setSelectedCities(prev => 
                          isSelected ? prev.filter(c => c !== city) : [...prev, city]
                        );
                      }}
                    >
                      <Text style={[styles.cityChipText, isSelected && styles.cityChipTextSelected]}>
                        {city}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
          )}
          {/* OSM preview controls */}
          <View style={styles.osmControls}>
            {osmLoading && (
              <View style={styles.osmRow}>
                <ActivityIndicator size="small" color="#0B61D6" />
                <Text style={{ fontSize: 12, color: '#6B7280' }}>正在加载 OSM 数据...</Text>
              </View>
            )}
            
            {osmError && (
              <View style={styles.osmErrorRow}>
                <Text style={styles.osmErrorText}>{osmError === 'rate-limited' ? 'OSM 被限流（429），请稍后重试' : (osmError === 'gateway-timeout' ? 'OSM 超时 (504)' : 'OSM 错误')}</Text>
                <TouchableOpacity onPress={() => setOsmRetry(r => r + 1)}>
                  <Text style={{ color: '#0B61D6', fontWeight: '700' }}>重试</Text>
                </TouchableOpacity>
              </View>
            )}

            <View style={{ flexDirection: 'column', gap: 12, width: '100%', marginTop: 8 }}>
              {/* Row 1: Get Location & Radius */}
              <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                <TouchableOpacity
                  style={[styles.osmGeoButton, osmGeoStatus === 'locating' && styles.osmDisabledBtn, { flex: 1, flexDirection: 'row', gap: 6 }]}
                  onPress={handleUseCurrentLocation}
                  disabled={osmGeoStatus === 'locating'}
                >
                  <PlatformIcon name="my_location" size={18} color="#374151" family="MaterialIcons" />
                  <Text style={{ color: '#374151', fontWeight: '600' }}>{osmGeoStatus === 'locating' ? '定位中...' : '获取位置'}</Text>
                </TouchableOpacity>
                <TextInput
                  placeholder="半径"
                  value={osmRadius}
                  onChangeText={setOsmRadius}
                  style={[styles.osmSmallInput, { flex: 1, textAlign: 'center' }]}
                  keyboardType="numeric"
                />
                <Text style={{ fontSize: 14, color: '#6B7280' }}>米/半径</Text>
              </View>

              {/* Row 2: Lat & Lon */}
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <TextInput
                  placeholder="纬度"
                  value={osmLatInput}
                  onChangeText={setOsmLatInput}
                  style={[styles.osmSmallInput, { flex: 1 }]}
                  keyboardType="decimal-pad"
                />
                <TextInput
                  placeholder="经度"
                  value={osmLonInput}
                  onChangeText={setOsmLonInput}
                  style={[styles.osmSmallInput, { flex: 1 }]}
                  keyboardType="decimal-pad"
                />
              </View>

              {/* Row 3: Actions */}
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <TouchableOpacity onPress={handleApplyManualCenter} style={[styles.osmActionBtn, styles.osmActionBtnPrimary, { flex: 1, alignItems: 'center', justifyContent: 'center' }]}>
                  <Text style={{ color: '#fff', fontWeight: '700' }}>应用坐标</Text>
                </TouchableOpacity>
                {osmCenter && (
                  <TouchableOpacity onPress={handleClearCenter} style={[styles.osmActionBtn, { paddingHorizontal: 16, alignItems: 'center', justifyContent: 'center' }]}>
                    <Text style={{ color: '#0B61D6', fontWeight: '700' }}>清除</Text>
                  </TouchableOpacity>
                )}
              </View>
            </View>

            {osmCenter && (
              <Text style={styles.osmHelperText}>
                {`已使用附近: ${osmCenter.lat.toFixed(4)}, ${osmCenter.lon.toFixed(4)} · 半径 ${Math.round(centerRadius ?? 1500)} 米`}
              </Text>
            )}
            {osmGeoError && (
              <Text style={[styles.osmHelperText, { color: '#DC2626' }]}>{osmGeoError}</Text>
            )}
          </View>
        </View>

        <View onLayout={(e) => setFiltersAnchorY(e.nativeEvent.layout.y)}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filtersContainer}>
            <View style={styles.filterGroup}>
              {cardTypes.map(type => (
                <TouchableOpacity
                  key={type.key}
                  style={[styles.filterChip, localSelectedTypes.includes(type.key) && styles.filterChipActive]}
                  onPress={() => handleTypeToggle(type.key)}
                >
                  <PlatformIcon name={type.icon} size={16} color={localSelectedTypes.includes(type.key) ? '#0B61D6' : '#6B7280'} />
                  <Text style={[styles.filterChipText, localSelectedTypes.includes(type.key) && styles.filterChipTextActive]}>{type.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </ScrollView>
        </View>

        <ScrollView
          ref={listTopRef}
          style={styles.content}
          showsVerticalScrollIndicator={false}
          onScroll={({ nativeEvent }: any) => {
            const { layoutMeasurement, contentOffset, contentSize } = nativeEvent;
            const isCloseToBottom = layoutMeasurement.height + contentOffset.y >= contentSize.height - 20;
            if (isCloseToBottom) loadMore();
          }}
          contentContainerStyle={[styles.contentInner, (bottomInset ? { paddingBottom: bottomInset } : (embedded ? { paddingBottom: 140 } : null))]}
          scrollEventThrottle={400}
        >
          <View style={{ position: 'relative' }}>
            <View style={[styles.cardsContainer, currentViewMode === 'grid' ? styles.cardsContainerGrid : styles.cardsContainerList]}>{displayedCards.map(renderCard)}</View>
            {paging && (
              <View style={styles.pageTransitionOverlay} pointerEvents="none">
                <ActivityIndicator size="small" color="#0B61D6" />
                <Text style={styles.pageTransitionText}>正在切換頁面...</Text>
              </View>
            )}
          </View>
          {loading && (<View style={styles.loadingContainer}><ActivityIndicator size="small" color="#0B61D6" /><Text style={styles.loadingText}>加载中...</Text></View>)}
          {!loading && !hasMore && displayedCards.length > 0 && (<View style={styles.noMoreContainer}><Text style={styles.noMoreText}>没有更多卡片了</Text></View>)}
          {!loading && displayedCards.length === 0 && (<View style={styles.emptyContainer}><PlatformIcon name="search_off" size={48} color="#9CA3AF" /><Text style={styles.emptyTitle}>未找到卡片</Text><Text style={styles.emptyText}>尝试调整搜索条件或过滤器</Text></View>)}

          {totalPages > 1 && (
            <View style={styles.paginationContainer}>
              <TouchableOpacity
                style={[styles.pageButton, page === 1 && styles.pageButtonDisabled]}
                onPress={() => {
                  if (page === 1) return;
                  setPage(1);
                  fetchCards(1, true, false, { notify: true });
                }}
              >
                <Text style={styles.pageButtonText}>«</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.pageButton, page === 1 && styles.pageButtonDisabled]}
                onPress={() => {
                  if (page === 1) return;
                  const p = Math.max(1, page - 1);
                  setPage(p);
                  fetchCards(p, true, false, { notify: true });
                }}
              >
                <Text style={styles.pageButtonText}>‹</Text>
              </TouchableOpacity>

              {getPageWindow(totalPages, page).map((p) => (
                <TouchableOpacity
                  key={p}
                  style={[styles.pageButton, page === p && styles.pageButtonActive]}
                  onPress={() => {
                    if (p === page) return;
                    setPage(p);
                    fetchCards(p, true, false, { notify: true });
                  }}
                >
                  <Text style={[styles.pageButtonText, page === p && styles.pageButtonTextActive]}>{p}</Text>
                </TouchableOpacity>
              ))}

              <TouchableOpacity
                style={[styles.pageButton, page === totalPages && styles.pageButtonDisabled]}
                onPress={() => {
                  if (page === totalPages) return;
                  const p = Math.min(totalPages, page + 1);
                  setPage(p);
                  fetchCards(p, true, false, { notify: true });
                }}
              >
                <Text style={styles.pageButtonText}>›</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.pageButton, page === totalPages && styles.pageButtonDisabled]}
                onPress={() => {
                  if (page === totalPages) return;
                  setPage(totalPages);
                  fetchCards(totalPages, true, false, { notify: true });
                }}
              >
                <Text style={styles.pageButtonText}>»</Text>
              </TouchableOpacity>
            </View>
          )}
        </ScrollView>
      </View>

      {openDetailInModal && (
        <Modal visible={detailModalVisible} transparent animationType="fade" onRequestClose={() => setDetailModalVisible(false)}>
          <View style={styles.modalBackdrop}>
            <View style={styles.modalContent}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>{detailCard?.title || '卡片详情'}</Text>
                <TouchableOpacity onPress={() => setDetailModalVisible(false)} accessibilityLabel="关闭" accessibilityRole="button"><PlatformIcon name="close" size={20} color="#6B7280" /></TouchableOpacity>
              </View>
              <ScrollView style={{ maxHeight: 480 }} contentContainerStyle={{ paddingBottom: 16 }}>
                <View style={styles.modalBody}>
                  <View style={styles.modalCover}>
                    {(() => {
                      const BROKEN_PLACEHOLDER = '/assets/placeholders/card-header-default.jpg';
                      const isValidImage = (url?: string) => url && url !== BROKEN_PLACEHOLDER;
                      
                      let modalImage = isValidImage(detailCard?.cover_image) ? detailCard?.cover_image :
                                       isValidImage(detailCard?.base_payload?.imageUrl) ? detailCard?.base_payload?.imageUrl :
                                       isValidImage(detailCard?.base_payload?.embed?.poster) ? detailCard?.base_payload?.embed?.poster : null;
                      
                      const coords = extractCoords(detailCard);

                      return modalImage ? (
                        <Image source={{ uri: modalImage }} style={{ width: '100%', height: '100%' }} resizeMode="cover" />
                      ) : (coords && detailCard) ? (
                         <View style={{ width: '100%', height: '100%', position: 'relative' }}>
                            <NearbyMap
                              center={[coords.lat, coords.lng]}
                              spots={[{
                                id: detailCard.id,
                                name: detailCard.title,
                                lat: coords.lat,
                                lng: coords.lng,
                                address: detailCard.address,
                                category: detailCard.card_type,
                                source: 'card'
                              }]}
                              height="100%"
                              showControls={false}
                              showFullscreenButton={true}
                              embedded={true}
                              enableOSM={false}
                              showInterPointDistances={false}
                            />
                         </View>
                      ) : (
                        <View style={styles.cardImagePlaceholder}><PlatformIcon name="image" size={32} color="#9CA3AF" /></View>
                      );
                    })()}
                  </View>
                  {detailCard?.address && (<View style={styles.modalRow}><PlatformIcon name="location_on" size={16} color="#6B7280" /><Text style={{ fontSize: 14, color: '#374151', marginLeft: 6, flex: 1 }} numberOfLines={2}>{detailCard.address}</Text></View>)}
                  {detailCard?.description && (<Text style={{ fontSize: 14, color: '#374151', marginTop: 12, lineHeight: 20 }}>{detailCard.description}</Text>)}
                  <View style={styles.modalActions}>
                    <TouchableOpacity onPress={() => setDetailModalVisible(false)} style={styles.modalBtn} accessibilityLabel="关闭" accessibilityRole="button"><Text style={styles.modalBtnText}>关闭</Text></TouchableOpacity>
                    {onSelectCard && detailCard && (
                      <TouchableOpacity onPress={() => { onSelectCard(detailCard); setDetailModalVisible(false); }} style={[styles.modalBtn, styles.modalBtnPrimary]} accessibilityLabel="加入行程" accessibilityRole="button"><Text style={[styles.modalBtnText, styles.modalBtnTextPrimary]}>加入行程</Text></TouchableOpacity>
                    )}
                  </View>
                </View>
              </ScrollView>
            </View>
          </View>
        </Modal>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#fff' },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 24, paddingVertical: 16, borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  title: { fontSize: 24, fontWeight: '800', color: '#111827' },
  titleEmbedded: { fontSize: 16, fontWeight: '700' },
  titleRow: { flexDirection: 'row', alignItems: 'center' },
  titleBar: { width: 4, height: 20, borderRadius: 2, marginRight: 10 },
  headerEmbedded: { paddingVertical: 8, borderBottomWidth: 0, paddingHorizontal: 16 },
  viewModeToggle: { flexDirection: 'row', backgroundColor: '#F3F4F6', borderRadius: 8, padding: 2 },
  viewModeToggleEmbedded: { padding: 1, borderRadius: 8 },
  viewModeBtn: { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 6 },
  viewModeBtnEmbedded: { paddingHorizontal: 6, paddingVertical: 4 },
  viewModeBtnActive: { backgroundColor: '#fff' },

  searchContainer: { paddingHorizontal: 24, paddingVertical: 16 },
  searchRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  searchBox: { flex: 1, flexDirection: 'row', alignItems: 'center', backgroundColor: '#F3F4F6', borderRadius: 20, paddingHorizontal: 16, paddingVertical: 8 },
  searchInput: { flex: 1, marginLeft: 12, fontSize: 16, color: '#111827', outlineWidth: 0, outlineColor: 'transparent', boxShadow: 'none' },
  filterButton: { width: 44, height: 44, borderRadius: 22, backgroundColor: '#F3F4F6', alignItems: 'center', justifyContent: 'center', position: 'relative' },
  filterButtonActive: { backgroundColor: '#E6F0FF' },
  filterBadge: { position: 'absolute', top: -4, right: -4, backgroundColor: ORANGE_ACTION, borderRadius: 10, minWidth: 20, height: 20, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 6 },
  filterBadgeText: { color: '#fff', fontSize: 11, fontWeight: '700' },
  cityFilterContainer: { marginTop: 12, backgroundColor: '#F9FAFB', borderRadius: 12, padding: 12, borderWidth: 1, borderColor: '#E5E7EB' },
  cityFilterHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 8 },
  cityChipsWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  cityChip: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20, backgroundColor: '#fff', borderWidth: 1, borderColor: '#E5E7EB' },
  cityChipSelected: { backgroundColor: '#0B61D6', borderColor: '#0B61D6' },
  cityChipText: { fontSize: 14, color: '#374151', fontWeight: '500' },
  cityChipTextSelected: { color: '#fff', fontWeight: '600' },

  filtersContainer: { paddingHorizontal: 24, marginBottom: 16 },
  filterGroup: { flexDirection: 'row', alignItems: 'center', marginRight: 16 },
  filterChip: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#F3F4F6', paddingHorizontal: 12, paddingVertical: 8, borderRadius: 20, marginRight: 8 },
  filterChipActive: { backgroundColor: '#E6F0FF' },
  filterChipText: { fontSize: 14, color: '#6B7280', marginLeft: 4 },
  filterChipTextActive: { color: '#0B61D6', fontWeight: '600' },

  content: { flex: 1 },
  contentInner: { paddingBottom: 48 },
  inner: { maxWidth: 600, width: '100%', alignSelf: 'center' },
  cardsContainer: { paddingHorizontal: 24 },
  cardsContainerGrid: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between' },
  cardsContainerList: { flexDirection: 'column' },
  pageTransitionOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255,255,255,0.8)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  pageTransitionText: { marginTop: 8, fontSize: 13, color: '#4B5563' },

  cardItem: {
    backgroundColor: '#fff',
    borderRadius: 12,
    marginBottom: 16,
    ...Platform.select({
      web: { boxShadow: '0px 2px 8px rgba(0,0,0,0.08)' },
      default: { shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 3 },
    }),
    position: 'relative' as any,
  },
  cardItemGrid: { width: '48%' },
  cardItemList: { width: '100%', flexDirection: 'row' },
  cardCover: { backgroundColor: '#F3F4F6', position: 'relative', overflow: 'hidden' },
  cardCoverGrid: { width: '100%', aspectRatio: 16 / 9, borderTopLeftRadius: 12, borderTopRightRadius: 12 },
  cardCoverList: { width: 140, aspectRatio: 16 / 9, borderTopLeftRadius: 12, borderBottomLeftRadius: 12, flexShrink: 0 },
  cardImagePlaceholder: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  videoPlayOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  videoPlayButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardTypeBadge: { position: 'absolute', top: 8, right: 8, backgroundColor: 'rgba(0, 0, 0, 0.6)', borderRadius: 12, padding: 4 },
  addFloatBtn: {
    position: 'absolute',
    top: 8,
    left: 8,
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: ORANGE_ACTION,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: { boxShadow: '0px 1px 6px rgba(0,0,0,0.12)' },
      default: { shadowColor: '#000', shadowOpacity: 0.15, shadowOffset: { width: 0, height: 1 }, shadowRadius: 2, elevation: 3 },
    }),
  },
  // 右侧纵向扁平 pill，仅列表模式
  listAddWrap: { position: 'absolute' as any, right: 0, top: 0, bottom: 0, width: 28, alignItems: 'center', justifyContent: 'center', pointerEvents: 'box-none' },
  listAddPill: { width: 28, height: '100%', borderTopLeftRadius: 999, borderBottomLeftRadius: 999, backgroundColor: ORANGE_ACTION, alignItems: 'center', justifyContent: 'center' },

  // OSM controls
  osmControls: { flexDirection: 'column', alignItems: 'flex-start', marginTop: 8, gap: 8 },
  osmRow: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  osmGeoRow: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  osmToggle: { paddingHorizontal: 10, paddingVertical: 6, borderRadius: 6, backgroundColor: '#F3F4F6', alignItems: 'center', justifyContent: 'center' },
  osmToggleActive: { backgroundColor: ORANGE_ACTION },
  osmCityInput: { marginLeft: 8, paddingHorizontal: 8, paddingVertical: 6, borderRadius: 6, backgroundColor: '#fff', borderWidth: 1, borderColor: '#E5E7EB', minWidth: 160 },
  osmGeoButton: { paddingHorizontal: 10, paddingVertical: 6, borderRadius: 6, backgroundColor: '#F3F4F6', alignItems: 'center', justifyContent: 'center' },
  osmDisabledBtn: { opacity: 0.6 },
  osmSmallInput: { paddingHorizontal: 8, paddingVertical: 6, borderRadius: 6, backgroundColor: '#fff', borderWidth: 1, borderColor: '#E5E7EB', minWidth: 120 },
  osmActionBtn: { paddingHorizontal: 10, paddingVertical: 6, borderRadius: 6, borderWidth: 1, borderColor: '#E5E7EB', backgroundColor: '#fff' },
  osmActionBtnPrimary: { backgroundColor: ORANGE_ACTION, borderColor: ORANGE_ACTION },
  osmHelperText: { marginTop: 4, fontSize: 12, color: '#6B7280' },
  osmErrorRow: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  osmErrorText: { color: '#DC2626' },

  // OSM badge
  osmBadge: { position: 'absolute', top: 8, left: 8, backgroundColor: '#34D399', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 6 },
  osmBadgeText: { color: '#fff', fontSize: 12, fontWeight: '700' },

  cardInfo: { padding: 12, flex: 1 },
  // 为右侧 pill 预留空间，避免地址被遮挡
  cardInfoListWithPill: { paddingRight: 52 },
  cardTitle: { fontSize: 16, fontWeight: '600', color: '#111827', marginBottom: 4 },
  privateBadge: { position: 'absolute', bottom: 8, right: 8, backgroundColor: 'rgba(79, 70, 229, 0.9)', borderRadius: 12, padding: 4 },
  cardAddress: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  cardAddressText: { fontSize: 14, color: '#6B7280', marginLeft: 4, flex: 1 },
  cardTagsContainer: { flexDirection: 'column' },
  cardTags: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center' },
  cardTag: { backgroundColor: '#F3F4F6', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4, marginRight: 4, marginBottom: 4 },
  cardTagText: { fontSize: 12, color: '#6B7280' },
  cardTagMore: { fontSize: 12, color: '#9CA3AF', marginLeft: 4 },

  loadingContainer: { flexDirection: 'row', justifyContent: 'center', alignItems: 'center', paddingVertical: 20 },
  loadingText: { marginLeft: 8, fontSize: 14, color: '#6B7280' },

  noMoreContainer: { alignItems: 'center', paddingVertical: 20 },
  noMoreText: { fontSize: 14, color: '#9CA3AF' },

  emptyContainer: { alignItems: 'center', paddingVertical: 60 },
  emptyTitle: { fontSize: 18, fontWeight: '600', color: '#111827', marginTop: 16, marginBottom: 8 },
  emptyText: { fontSize: 14, color: '#6B7280', textAlign: 'center' },

  paginationContainer: { flexDirection: 'row', justifyContent: 'center', alignItems: 'center', paddingVertical: 16, paddingHorizontal: 12, flexWrap: 'nowrap', alignSelf: 'center', marginHorizontal: 12 },
  pageButton: { minWidth: 36, paddingHorizontal: 10, paddingVertical: 8, borderRadius: 8, marginHorizontal: 4, borderWidth: 1, borderColor: '#E5E7EB', backgroundColor: '#fff', alignItems: 'center', justifyContent: 'center' },
  pageButtonActive: { backgroundColor: ORANGE_ACTION, borderColor: ORANGE_ACTION },
  pageButtonDisabled: { opacity: 0.5 },
  pageButtonText: { fontSize: 14, color: '#111827' },
  pageButtonTextActive: { color: '#fff', fontWeight: '700' },

  // Modal styles
  modalBackdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', alignItems: 'center', justifyContent: 'center', paddingHorizontal: 16 },
  modalContent: { width: '100%', maxWidth: 640, backgroundColor: '#fff', borderRadius: 12, overflow: 'hidden' },
  modalHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  modalTitle: { fontSize: 16, fontWeight: '700', color: '#111827' },
  modalBody: { padding: 16 },
  modalCover: { width: '100%', height: 200, backgroundColor: '#F3F4F6', borderRadius: 8, marginBottom: 12, overflow: 'hidden' },
  modalRow: { flexDirection: 'row', alignItems: 'center', marginTop: 8 },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 16 },
  modalBtn: { paddingHorizontal: 12, paddingVertical: 10, borderRadius: 8, borderWidth: 1, borderColor: '#E5E7EB', backgroundColor: '#fff' },
  modalBtnPrimary: { backgroundColor: ORANGE_ACTION, borderColor: ORANGE_ACTION },
  modalBtnText: { fontSize: 14, color: '#111827' },
  modalBtnTextPrimary: { color: '#fff', fontWeight: '700' },
});

export default CardList;
